import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
// Video (optimized files + poster)
import SampleVideo720 from '../../assets/video/commercial-sample-video-siv-renate-720.mp4';
import SampleVideo1080 from '../../assets/video/commercial-sample-video-siv-renate-1080.mp4';
import Poster from '../../assets/images/posters/commercial-sample-video-siv-renate-poster.webp';
// Constants
import { CompanyName } from '../../utils/Constants';
import { BOOKING_PAGE_URL, COMMERCIALS_PAGE_URL } from '../../utils/Routes';
// Icons
import { MdDoubleArrow } from 'react-icons/md';
import { FaClock, FaRegCommentDots, FaUserTie } from 'react-icons/fa';

function HomeCommericalSection() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const tryPlay = () => {
      const p = v.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    // Autoplay as soon as possible
    const onReady = () => tryPlay();
    v.addEventListener('canplay', onReady, { once: true });
    v.addEventListener('loadeddata', onReady, { once: true });

    // Pause when out of view; resume when back
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) tryPlay();
          else v.pause();
        });
      },
      { rootMargin: '100px 0px 100px 0px', threshold: 0.2 }
    );
    io.observe(v);

    // Pause on hidden tab; resume if visible and in view
    const onVis = () => {
      if (document.hidden) {
        v.pause();
        return;
      }
      const r = v.getBoundingClientRect();
      const inView =
        r.bottom >= 1 &&
        r.right >= 1 &&
        r.top <= (window.innerHeight || document.documentElement.clientHeight) &&
        r.left <= (window.innerWidth || document.documentElement.clientWidth);
      if (inView) tryPlay();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      v.removeEventListener('canplay', onReady);
      v.removeEventListener('loadeddata', onReady);
      io.disconnect();
    };
  }, []);

  return (
    <section className='grid gap-6 lg:gap-y-12 px-8 py-8 xl:py-24 2xl:px-32 lg:px-16'>
      <div className='grid gap-y-4 lg:gap-y-6'>
        <h3 className='font-titleFont text-2xl lg:text-3xl text-center underline underline-offset-8'>
          COMMERICALS
        </h3>
        <h4 className='text-center lg:text-xl italic font-serif'>
          by {CompanyName}
        </h4>
      </div>

      {/* Top row with video and text */}
      <div className='grid lg:grid-cols-2 gap-8'>
        {/* video */}
        <section className='grid rounded-3xl overflow-hidden shadow-lg'>
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload='auto'
            poster={Poster}
            disablePictureInPicture
            controlsList='nodownload noremoteplayback nofullscreen'
            className='w-full h-full object-cover'
            aria-label={`Sample commerical by ${CompanyName}`}
          >
            {/* Mobile-first source, desktop upgrade */}
            <source src={SampleVideo720} type='video/mp4' />
            <source src={SampleVideo1080} type='video/mp4' media='(min-width: 1024px)' />
            Your browser does not support the video tag.
          </video>
        </section>

        {/* article */}
        <section className='grid bg-blue-100 text-colour2 rounded-3xl shadow-lg p-8 place-items-center'>
          <article className='grid gap-4 text-center'>
            <h2 className='text-3xl font-bold font-titleFont'>
              Commercial Creation
            </h2>
            <p className='text-lg'>
              I craft high-quality commercials designed to capture attention,
              tell your brand’s story, and leave a lasting impression. From
              concept to final cut, our team ensures a polished and professional
              result.
            </p>
            <p>Contact me today to solve your needs</p>
            <div className='grid px-4 justify-center'>
              <Link
                to={BOOKING_PAGE_URL}
                className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
                aria-label={`Book ${CompanyName}`}
                rel='bookmark'
                title='Booking'
              >
                <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
                  <span> BOOK NOW</span>
                  <MdDoubleArrow className='text-xl lg:text-2xl' />
                </div>
              </Link>
            </div>
          </article>
        </section>
      </div>

      {/* Second row with features */}
      <div className='grid lg:grid-cols-3 gap-8'>
        <aside className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6 text-center place-items-center'>
          <FaClock className='text-4xl text-colour5' />
          <h3 className='text-xl font-semibold font-titleFont'>
            Timely Delivery
          </h3>
          <span className='text-sm italic text-colour5 opacity-80'>
            Always on schedule
          </span>
          <p className='text-sm opacity-80'>
            Fast turnaround without sacrificing quality — we respect your
            deadlines.
          </p>
        </aside>

        <aside className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6 text-center place-items-center'>
          <FaRegCommentDots className='text-4xl text-colour5' />
          <h3 className='text-xl font-semibold font-titleFont'>
            Collaborative Review
          </h3>
          <span className='text-sm italic text-colour5 opacity-80'>
            Your input matters
          </span>
          <p className='text-sm opacity-80'>
            Open feedback loops to ensure the final product matches your vision.
          </p>
        </aside>

        <aside className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6 text-center place-items-center'>
          <FaUserTie className='text-4xl text-colour5' />
          <h3 className='text-xl font-semibold font-titleFont'>
            Professionalism
          </h3>
          <span className='text-sm italic text-colour5 opacity-80'>
            Polished & reliable
          </span>
          <p className='text-sm opacity-80'>
            Experienced creatives who bring polish, style, and expertise to
            every project.
          </p>
        </aside>
      </div>

      <div className='grid px-4 justify-center'>
        <Link
          to={COMMERCIALS_PAGE_URL}
          className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
          aria-label={`Commercials by ${CompanyName}`}
          rel='bookmark'
          title='Commercials'
        >
          <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
            <span>VISIT PAGE</span>
            <MdDoubleArrow className='text-xl lg:text-2xl' />
          </div>
        </Link>
      </div>
    </section>
  );
}

export default HomeCommericalSection;
